import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { toCloudinarySrc } from '@peloton/images';
import type { Artist } from '@engage/api-v2';
import { gray2, slate1 } from '@engage/colors';
import { spaces } from '@engage/styles';
import { MusicNote } from '@members/video-ui/icons';

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

export const ArtistImage: React.FC<
  React.PropsWithChildren<{
    hasShadow: boolean;
    artist: Artist;
    width?: number;
  }>
> = ({ hasShadow, width, artist: { imageUrl, name } }) => {
  const [state, setState] = useState('initial');
  const imageSrc = imageUrl
    ? toCloudinarySrc({ src: imageUrl, width: width ?? spaces.huge })
    : undefined;

  return (
    <Container hasShadow={hasShadow && state === 'loaded'}>
      {imageSrc ? (
        <GhostImage
          src={imageSrc}
          alt={name}
          onLoad={() => setState('loaded')}
          onError={() => setState('initial')}
        />
      ) : null}
      {imageSrc && state === 'loaded' ? (
        <Image src={imageSrc} alt={name} />
      ) : (
        <MusicNote stroke={slate1} />
      )}
    </Container>
  );
};

const Container = styled.div<{ hasShadow: boolean }>`
  width: 100%;
  height: 100%;
  background-color: ${gray2};
  border-radius: ${spaces.xSmall}px;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
  filter: ${({ hasShadow }) =>
    hasShadow ? 'drop-shadow(0px 2px 8px rgba(0, 0, 0, 0.16))' : 'none'};
`;

const GhostImage = styled.img`
  width: 100%;
  height: 100%;
  position: absolute;
  display: none;
`;

const Image = styled.img`
  max-height: ${spaces.huge};
  max-width: ${spaces.huge};
  animation: ${fadeIn} 250ms;
`;
